import { type PlanKey, config, isPricingV2Live } from "@repo/config";
import { getEntitlements } from "@repo/payments/client";

/**
 * One plan as marketing copy quotes it: the monthly price and the monthly
 * allowances, read from the same plan config and entitlements table the app
 * bills with.
 */
export interface MarketingPlanPrice {
	planKey: PlanKey;
	/** Monthly price in USD (the `intervalCount === 1` monthly price). */
	monthlyPrice: number;
	/** Per-month equivalent of the quarterly price, rounded to whole dollars. */
	quarterlyMonthlyPrice: number;
	/** Per-month equivalent of the yearly price, rounded to whole dollars. */
	yearlyMonthlyPrice: number;
	/** Prompts tracked daily. */
	includedPrompts: number;
	monthlyCredits: number;
	siteAuditPages: number;
}

export interface MarketingPlanPrices {
	brand: MarketingPlanPrice;
	agencyCore: MarketingPlanPrice;
	/** Only sold once the October 2026 pricing is live. */
	agencyPro?: MarketingPlanPrice;
}

/**
 * The per-month price of a plan billed every `months` months: 1 (monthly),
 * 3 (quarterly) or 12 (yearly), rounded to whole dollars.
 */
function perMonthPriceOf(planKey: PlanKey, months: 1 | 3 | 12): number {
	const plan = config.payments.plans[planKey] as {
		prices?: ReadonlyArray<{
			type: string;
			interval?: string;
			intervalCount?: number;
			amount: number;
		}>;
	};
	const price = plan.prices?.find((p) => {
		if (p.type !== "recurring") return false;
		const count = p.intervalCount ?? 1;
		if (months === 12) return p.interval === "year" && count === 1;
		return p.interval === "month" && count === months;
	});
	return price ? Math.round(price.amount / months) : 0;
}

function planPrice(planKey: PlanKey): MarketingPlanPrice {
	const entitlements = getEntitlements(planKey);
	return {
		planKey,
		monthlyPrice: perMonthPriceOf(planKey, 1),
		quarterlyMonthlyPrice: perMonthPriceOf(planKey, 3),
		yearlyMonthlyPrice: perMonthPriceOf(planKey, 12),
		includedPrompts: entitlements.trackedPrompts,
		monthlyCredits: entitlements.monthlyCredits,
		siteAuditPages: entitlements.siteAuditPages,
	};
}

/**
 * The plans marketing pages quote, for whichever pricing generation is on
 * sale: the October 2026 plans when `NEXT_PUBLIC_PRICING_V2=1`, the v1 plans
 * before. Copy that reads from here is right before and after launch.
 */
export function getMarketingPlanPrices(): MarketingPlanPrices {
	if (isPricingV2Live()) {
		return {
			brand: planPrice("brandV2"),
			agencyCore: planPrice("agencyCoreV2"),
			agencyPro: planPrice("agencyPro"),
		};
	}
	return {
		brand: planPrice("brand"),
		agencyCore: planPrice("agencyCoreUsageBased"),
	};
}
